import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import {
  AuthenticationDetails,
  CognitoUser,
  type IAuthenticationCallback,
} from "amazon-cognito-identity-js";
import { getPool } from "./cognito.js";

// No MFA for students (pool has MFA OFF) - just signedOut -> [newPassword] -> signedIn.
// Forgot-password is a side branch: signedOut -> forgot (enter email) -> reset
// (enter emailed code + new password) -> signedOut (with a success notice).
export type StudentAuthStatus =
  | "loading"
  | "signedOut"
  | "newPassword"
  | "signedIn"
  | "forgot"
  | "reset";

interface StudentAuthState {
  status: StudentAuthStatus;
  email: string;
  error: string;
  notice: string;
  signIn(email: string, password: string): void;
  completeNewPassword(newPassword: string): void;
  startForgot(): void;
  requestReset(email: string): void;
  confirmReset(code: string, newPassword: string): void;
  backToSignIn(): void;
  signOut(): void;
}

const Ctx = createContext<StudentAuthState | null>(null);
export const useStudentAuth = () => {
  const v = useContext(Ctx);
  if (!v) throw new Error("useStudentAuth outside provider");
  return v;
};

export function StudentAuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<StudentAuthStatus>("loading");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const userRef = useRef<CognitoUser | null>(null);

  useEffect(() => {
    const u = getPool()?.getCurrentUser();
    if (!u) return setStatus("signedOut");
    u.getSession((err: Error | null, s: { isValid(): boolean } | null) => {
      setStatus(err || !s?.isValid() ? "signedOut" : "signedIn");
    });
  }, []);

  const callbacks = (): IAuthenticationCallback => ({
    onSuccess: () => { setError(""); setStatus("signedIn"); },
    onFailure: (err) => setError(err?.message ?? "Sign-in failed"),
    newPasswordRequired: () => { setError(""); setStatus("newPassword"); },
  });

  function signIn(em: string, password: string) {
    setError("");
    const pool = getPool();
    if (!pool) { setError("The student portal isn't configured yet."); return; }
    const user = new CognitoUser({ Username: em, Pool: pool });
    userRef.current = user;
    setEmail(em);
    user.authenticateUser(new AuthenticationDetails({ Username: em, Password: password }), callbacks());
  }

  function completeNewPassword(newPassword: string) {
    setError("");
    userRef.current?.completeNewPasswordChallenge(newPassword, {}, callbacks());
  }

  function startForgot() {
    setError("");
    setNotice("");
    setStatus("forgot");
  }

  // Ask Cognito to email a reset code, then move to the code + new-password form.
  function requestReset(em: string) {
    setError("");
    const pool = getPool();
    if (!pool) { setError("The student portal isn't configured yet."); return; }
    const user = new CognitoUser({ Username: em, Pool: pool });
    userRef.current = user;
    setEmail(em);
    user.forgotPassword({
      onSuccess: () => setStatus("reset"),
      inputVerificationCode: () => setStatus("reset"),
      onFailure: (err) => setError(err?.message ?? "Couldn't start the reset. Check the email address."),
    });
  }

  // Confirm the emailed code + new password, then return to sign-in.
  function confirmReset(code: string, newPassword: string) {
    setError("");
    const user = userRef.current;
    if (!user) { setError("Start again from Forgot password."); setStatus("forgot"); return; }
    user.confirmPassword(code.trim(), newPassword, {
      onSuccess: () => {
        setStatus("signedOut");
        setNotice("Password changed. Sign in with your new password.");
      },
      onFailure: (err) => setError(err?.message ?? "Reset failed. Check the code and try again."),
    });
  }

  function backToSignIn() {
    setError("");
    setNotice("");
    setStatus("signedOut");
  }

  function signOut() {
    getPool()?.getCurrentUser()?.signOut();
    userRef.current = null;
    setStatus("signedOut");
  }

  return (
    <Ctx.Provider
      value={{
        status, email, error, notice,
        signIn, completeNewPassword,
        startForgot, requestReset, confirmReset, backToSignIn,
        signOut,
      }}
    >
      {children}
    </Ctx.Provider>
  );
}
